import React, { useState } from "react";
import { Button } from "components/ui/button";
import { SavedCardItem } from "./SavedCardItem";
import type { PaymentMethod } from "./types";

const INITIAL_VISIBLE_COUNT = 3;

interface SavedCardSelectorProps {
  paymentMethods: PaymentMethod[];
  selectedId: number | "new";
  onSelect: (id: number | "new") => void;
  onAddCard: () => void;
  canAddMore?: boolean;
}

// Card-like panel matching the sibling OrderDetailsDrawer (shadcn/Tailwind).
const PANEL = "flex flex-col gap-4 rounded-xl border border-solid border-border bg-background p-6";
// L2 panel/section title — one step below the L1 view header (text-lg).
const PANEL_TITLE = "text-foreground m-0 text-base font-semibold leading-6";

function ChevronDownIcon(): React.ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M5 7.5L10 12.5L15 7.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export const SavedCardSelector: React.FC<SavedCardSelectorProps> = ({
  paymentMethods,
  selectedId,
  onSelect,
  onAddCard,
  canAddMore = true,
}) => {
  const [showAll, setShowAll] = useState(false);
  const hasMore = paymentMethods.length > INITIAL_VISIBLE_COUNT;
  const visibleCards = showAll ? paymentMethods : paymentMethods.slice(0, INITIAL_VISIBLE_COUNT);

  return (
    <>
      {/* Card tiles list */}
      <div className={PANEL}>
        <h3 className={PANEL_TITLE}>Pick a credit card to continue</h3>

        <div className="flex flex-col gap-4">
          {visibleCards.map((pm) => (
            <SavedCardItem key={pm.id} paymentMethod={pm} onClick={() => onSelect(pm.id)} />
          ))}
        </div>

        {hasMore && (
          <button
            type="button"
            className="text-foreground flex items-center justify-center gap-1 pt-2 text-sm font-medium hover:underline"
            onClick={() => setShowAll((prev) => !prev)}
          >
            <span
              className={`flex h-5 w-5 items-center justify-center transition-transform ${showAll ? "rotate-180" : ""}`}
            >
              <ChevronDownIcon />
            </span>
            {showAll ? "Show less" : "Show more"}
          </button>
        )}
      </div>

      {/* Add a new card section */}
      <div className={PANEL}>
        <h3 className={PANEL_TITLE}>Add a new card</h3>
        <p className="text-muted-foreground -mt-3 text-base font-normal leading-6">
          You can also add a new card to pay with it.
        </p>
        <Button
          type="button"
          variant="outline"
          className="box-border h-11 w-full rounded-xl"
          onClick={onAddCard}
          disabled={!canAddMore}
        >
          {canAddMore ? "Add card" : "Card limit reached"}
        </Button>
      </div>
    </>
  );
};
